/**
 * @file GlucometryRecordsView.jsx
 * @author Fernando Ruano
 * @description Glucometry Records View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import {
  PlusCircleOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { groupBy } from 'lodash';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import ModalOptions from '@nephro/components/ModalOptions';
import ConfirmModal from '@nephro/components/ConfirmModal';
import ModalInputsG from '@nephro/pages/PatientRecords/Components/ModalInputsG/modalInputsG';

// Helpers
import {
  glucometryValidation,
  sortByDate,
  onGetItemById
} from '@nephro/commons/helpers/utils';

// Styles
import { Empty } from 'antd';
import styles from '../../styles.module.css';

function GlucometryRecordsView({ glucometryActions, patientData, isPacient }) {
  const [visible, setVisible] = React.useState(false);
  const [visibleConfirm, setVisibleConfirm] = React.useState(false);
  const [value, setValue] = React.useState(null);
  const [measurement, setMeasurement] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(moment());
  const [selectedTime, setSelectedTime] = React.useState(moment());
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [visibleDrawer, setVisibleDrawer] = React.useState(false);
  const [glucometryList, setGlucometryList] = React.useState({});

  const [glucometryTimesList, setGlucometryTimesList] = React.useState(null);
  const [glucometryDates, setGlucometryDates] = React.useState([]);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const scrollRef = React.useRef(null);
  const { t: translate } = useTranslation();

  const {
    createGlucometry,
    getGlucometryByPatientId,
    deleteGlucometryById,
    updateGlucometry,
    getGlucometryTimesArray
  } = glucometryActions;

  const scrollToBottom = () => {
    // scrollRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const formatDate = (date) => moment(date).format('YYYY-MM-DD');
  const formatTime = (date) => moment(date || moment()).format('HH:mm:ss');

  // Function to get and set glucometry list
  const onGetGlucometryList = (_, response) => {
    if (response?.data) {
      let list = response?.data;

      list = sortByDate(list, 'example_date');

      let newGlucometryList = list.map((itm) => {
        return {
          date: formatDate(itm.example_date),
          ...itm
        };
      });
      newGlucometryList = groupBy(newGlucometryList, 'date');
      const datesKeys = Object.keys(newGlucometryList);

      setGlucometryList(newGlucometryList);
      setGlucometryDates(datesKeys);
    }
  };

  // Get glucometry list
  const getGlucometries = () =>
    getGlucometryByPatientId(
      { id: patientData?.id_patient },
      onGetGlucometryList
    );

  // Create glucometry record
  const onDeleteGlucometry = () => {
    deleteGlucometryById(
      { id: selectedItem.id_bglucometry_control },
      getGlucometries
    );
    setVisibleConfirm(false);
  };

  const onUpdateGlucometry = () => {
    const glucometryTime = onGetItemById(
      glucometryTimesList,
      'id_glucometry_time',
      measurement.value
    );
    const exampleDate = `${formatDate(selectedDate)}T${formatTime(
      selectedTime
    )}`;
    const data = {
      id: selectedItem.id_bglucometry_control,
      id_bglucometry_control: selectedItem.id_bglucometry_control,
      example_date: exampleDate,
      glucometry_value: `${value}`,
      glucometry_time: glucometryTime,
      patient_colombia: {
        ...selectedItem.patient_colombia
      }
    };
    setVisibleConfirm(false);
    updateGlucometry(data, getGlucometries);
  };

  const onSetItems = (itm) => {
    setValue(itm?.glucometry_value);
    setMeasurement({
      children: itm?.glucometry_time?.title,
      value: itm?.glucometry_time?.id_glucometry_time
    });
    setSelectedDate(moment(itm.example_date));
    setSelectedTime(moment(itm.example_date));
  };

  const onOpenEdit = (itm) => {
    setVisibleDrawer(false);
    setIsDeleting(false);
    onSetItems(itm);
    setIsEditing(true);
    setVisible(true);
  };

  const onOpenDelete = (itm) => {
    setVisibleDrawer(false);
    setIsEditing(false);
    onSetItems(itm);
    setIsDeleting(true);
    setVisibleConfirm(true);
  };

  const modalOptions = [
    {
      icon: <EditOutlined />,
      textOption: 'patientRecords.edit',
      type: 'primary',
      onClick: () => onOpenEdit(selectedItem)
    },
    {
      icon: <DeleteOutlined style={{ color: 'var(--color-error)' }} />,
      textOption: 'patientRecords.delete',
      type: 'error',
      onClick: () => onOpenDelete(selectedItem)
    }
  ];

  // After create glucometry record
  const afterCreateGlucometry = (type) => {
    if (type === 'success') {
      getGlucometries();
    }
  };

  // Create glucometry record
  const onCreateGlucometry = () => {
    const glucometryTime = onGetItemById(
      glucometryTimesList,
      'id_glucometry_time',
      measurement.value
    );
    const exampleDate = `${formatDate(selectedDate)}T${formatTime(
      selectedTime
    )}`;
    const data = {
      example_date: exampleDate,
      glucometry_value: `${value}`,
      glucometry_time: glucometryTime,
      patient_colombia: {
        ...patientData
      }
    };
    setVisibleConfirm(false);
    createGlucometry(data, afterCreateGlucometry);
  };

  const glucometryStateColor = (val) =>
    glucometryValidation(val) === 'normal' ? 'text-baxterGray' : 'text-error';

  const openMore = (itm) => {
    setVisibleDrawer(true);
    setSelectedItem(itm);
  };

  const clearInputs = () => {
    setValue(null);
    setMeasurement(null);
    setSelectedDate(moment());
    setSelectedTime(moment());
  };

  const openCreateNew = () => {
    setIsEditing(false);
    clearInputs();
    setVisible(true);
  };

  const validateDateText = (date) => {
    const today = formatDate(moment());
    const yesterday = formatDate(moment().subtract(1, 'days'));
    switch (date) {
      case today:
        return translate('calendarMg.today'); // Today
      case yesterday:
        return translate('patientRecords.yesterday'); // Yesterday
      default:
        return moment(date).format('D MMMM, YYYY'); // Past days
    }
  };

  const onConfirmCreate = () => {
    setVisible(false);
    setIsDeleting(false);
    setVisibleConfirm(true);
  };

  const onSubmitConfirm = () => {
    if (isEditing) onUpdateGlucometry(selectedItem);
    if (isDeleting) onDeleteGlucometry(selectedItem);
    if (!isEditing && !isDeleting) onCreateGlucometry(selectedItem);
  };

  // Function to get and set glucometry list
  const onGetGlucometryTimesList = (_, response) => {
    if (response?.data) {
      setGlucometryTimesList(
        response?.data.sort(
          (a, b) => a.id_glucometry_time - b.id_glucometry_time
        )
      );
    }
  };

  // Get glucometry list
  const getGlucometryTimes = () =>
    getGlucometryTimesArray(onGetGlucometryTimesList);

  React.useEffect(() => {
    getGlucometries();
    getGlucometryTimes();
  }, []);

  React.useEffect(() => {
    scrollToBottom();
  }, [glucometryList]);

  const getGlucometryValue = (val) => {
    if (Number(val) === 0) {
      return 'Low';
    }
    if (Number(val) === 999) {
      return 'High';
    }
    return `${val} mg/dL`;
  };

  const sortByGlucometryTime = (list) => {
    return list.sort(
      (a, b) =>
        a.glucometry_time.id_glucometry_time -
        b.glucometry_time.id_glucometry_time
    );
  };

  const imBadText = 'Me estoy sintiendo mal';
  const hasTimeValidation = measurement?.children === imBadText;
  return (
    <div className="records-menu">
      {isPacient && (
        <div className="pl-5 pr-5">
          <ButtonFilled
            key="1"
            type="cyan"
            onActionClick={openCreateNew}
            classStyles={[styles.actionButtonCreate].join(' ')}
            textButton="patientRecords.create"
            iconComponent={<PlusCircleOutlined className={styles.buttonIcon} />}
          />
          <h3 className="text-sm text-baxterGray mt-6">
            {translate('patientRecords.textContentGlucometry')}
          </h3>
        </div>
      )}
      {Object.entries(glucometryList).length === 0 && (
        <div className="mt-40">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={translate('commons.noData')}
            style={{ color: '#858fab', fontSize: 15 }}
          />
        </div>
      )}
      {glucometryDates.map((itmDate, index1) => {
        return (
          <div key={index1}>
            <h2 className="mb-3 mt-7 text-lg font-bold text-baxterGray pl-5">
              {validateDateText(itmDate)}
            </h2>
            <div
              className="w-full flex overflow-x-scroll pb-5 pl-5"
              ref={scrollRef}>
              {glucometryList[itmDate] !== undefined &&
                sortByGlucometryTime(glucometryList[itmDate]).map(
                  (itm, index2) => {
                    const glucometryValue = itm?.glucometry_value;
                    return (
                      <div key={index2} className={styles.recordsCard}>
                        <div className="flex flex-row justify-between items-center">
                          <h3
                            className={`mb-0 text-sm font-bold ${glucometryStateColor(
                              glucometryValue
                            )}`}>
                            {getGlucometryValue(glucometryValue)}
                          </h3>
                          {isPacient && (
                            <MoreOutlined
                              onClick={() => openMore(itm)}
                              style={{
                                fontSize: 24,
                                color: 'var(--color-baxter-gray)'
                              }}
                            />
                          )}
                        </div>
                        <h3 className="mb-0 text-sm text-baxterGray">
                          {itm?.glucometry_time?.title}
                        </h3>
                        {itm?.glucometry_time?.title === imBadText && (
                          <h3 className="mb-0 text-sm text-baxterGray">
                            {moment(itm?.example_date).format('h:mm a')}
                          </h3>
                        )}
                      </div>
                    );
                  }
                )}
            </div>
          </div>
        );
      })}
      <ConfirmModal
        visibleConfirm={visibleConfirm}
        onCancel={() => setVisibleConfirm(false)}
        mode={isDeleting ? 'delete' : 'confirm'}
        onSubmit={onSubmitConfirm}
        onSubmitText={
          isDeleting ? 'patientRecords.delete' : 'patientRecords.confirm'
        }
        contentComponent={
          <div className="bg-highLight flex flex-col justify-center items-center p-2 rounded-md mt-2">
            <h3 className="text-base text-secondary mb-0">
              {measurement?.children}
            </h3>
            <h3 className="text-base text-secondary mb-0 mt-2">
              {moment(selectedDate).format('D MMMM, YYYY')}
              {hasTimeValidation &&
                `- ${moment(selectedTime || moment()).format('h:mm a')}`}
            </h3>
            <h3 className="text-base text-secondary mb-0 mt-2">
              {getGlucometryValue(value)}
            </h3>
          </div>
        }
      />
      <ModalInputsG
        isEditing={isEditing}
        visible={visible}
        setVisible={setVisible}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedTime={selectedTime}
        setSelectedTime={setSelectedTime}
        measurement={measurement}
        setMeasurement={setMeasurement}
        value={value}
        setValue={setValue}
        onConfirmCreate={onConfirmCreate}
        glucometryTimesList={glucometryTimesList}
      />
      <ModalOptions
        modalOptions={modalOptions}
        visible={visibleDrawer}
        setVisible={setVisibleDrawer}
      />
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
GlucometryRecordsView.propTypes = {
  glucometryActions: PropTypes.shape({
    getGlucometryTimesArray: PropTypes.func.isRequired,
    getGlucometryByPatientId: PropTypes.func.isRequired,
    createGlucometry: PropTypes.func.isRequired,
    getGlucometryById: PropTypes.func.isRequired,
    updateGlucometry: PropTypes.func.isRequired,
    deleteGlucometryById: PropTypes.func.isRequired
  }).isRequired,
  patientData: PropTypes.object,
  isPacient: PropTypes.bool
};

export default GlucometryRecordsView;
